import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';

import {Alert, Button, Container, Grid, Header, Icon, Link, RadioGroup, SpaceBetween,} from '@amzn/awsui-components-react-v3';

import {isEmpty} from 'lodash';
import {v4 as uuidv4} from 'uuid';

import {RECEIVE_TYPES_UI} from '../../constants';
import {getSiteDeprecationStatus} from '../../constants/siteDeprecations';
import {Context, Types} from '../../store';
import {
  Model,
  ReceiveExecutionType,
  ReceiveType,
  ReferenceQueryResponseItem,
  Site,
  URIParams,
  ValidationStatusType,
} from '../../store/types';
import SiteInput from '../SiteInput';
import {ValueWithLabel} from '../Utils';
import {
  CYCLE_COUNT_ADJUSTMENT_DESCRIPTION,
  CYCLE_COUNT_ADJUSTMENT_LABEL,
  CYCLE_COUNT_ADJUSTMENT_VALUE,
  RECOVERED_GEAR_DESCRIPTION,
  RECOVERED_GEAR_LABEL,
  RECOVERED_GEAR_VALUE,
  REHARVESTED_GEAR_DESCRIPTION,
  REHARVESTED_GEAR_DESCRIPTION_NOTE,
  REHARVESTED_GEAR_LABEL,
  REHARVESTED_GEAR_VALUE,
} from './constants';
import '../../styles.scss';
import {WidgetType} from "../../widgets/widgetCommon/widgetConstants";
import {
  BoostMetaDataInventoryAdjustmentRequest,
  updateBoostMetaDataInventoryAdjustment
} from "../../boost/utils";
import {AppContext, AppType} from "../../store/appContext";

interface InventoryAdjustmentProps {
  preferredSite: Site;
}

export default function InventoryAdjustment({ preferredSite }: InventoryAdjustmentProps) {
  const history = useHistory();
  const urlParam = useParams<URIParams>();
  const { state, dispatch } = useContext(Context);
  const isBoostWidget = state.widgetType === WidgetType.INVENTORY_ADJUSTMENT;

  const appContext = useContext(AppContext);
  const mountRef = useRef<Boolean>(false);

  const [showNoteAlert, setShowNoteAlert] = useState(false);

  const references: ReferenceQueryResponseItem[] = state?.receiveReferences || [];
  const reference = references.find(
    (ref: ReferenceQueryResponseItem) => ref.receiveReference.receiveReferenceId === urlParam?.referenceId,
  );

  const model: Model = state?.models?.find((m: Model) => m?.referenceId === urlParam.referenceId);
  const modelExists = !!model;

  const [reason, setReason] = React.useState(
    !isEmpty(reference?.receiveReference.reasonCode) && reference ? reference?.receiveReference.reasonCode : '',
  );

  const [selectedSiteOption, setSelectedSiteOption] = useState<any>( () => {
        const siteName = reference?.receiveReference?.siteName;
        const selectedSiteInfo = {
          label : siteName,
          value: siteName,
          locationId: reference?.receiveReference?.expectedReceiveLocation
        }
        return !isEmpty(reference?.receiveReference?.siteName) && reference ? selectedSiteInfo : null
      }
  );

  const updateBoostMetaDataFn = useCallback(
      (inventoryAdjustmentReason?: string, siteOption?: any) => {
        if (appContext.appType === AppType.ARGO) {
          return;
        }

        let siteName = siteOption?.value;
        if (!siteName) {
          siteName = reference?.receiveReference.siteName || reference?.siteName;
        }

        const iaReason = inventoryAdjustmentReason ?? reason;

        if (siteName && iaReason && siteOption?.locationId) {
          const metaData: BoostMetaDataInventoryAdjustmentRequest = {
            receivingSite: siteName,
            inventoryAdjustmentType: iaReason,
            receiveType: ReceiveExecutionType.INVENTORY_ADJUSTMENT
          };
          updateBoostMetaDataInventoryAdjustment(state?.workRequestId, metaData);
        }
      },
      [
        state?.workRequestId,
        reference?.siteName,
        reference?.receiveReference,
        appContext.appType,
        reason
      ],
  );

  useEffect(() => {
    // on mount update boost initial meta data
    if (reference && !mountRef.current) {
      mountRef.current = true;
      updateBoostMetaDataFn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateBoostMetaDataFn, reference]);

  const onHandleConfirmClear = () => {
    dispatch({
      type: Types.clearReference,
      payload: {
        referenceId: '',
      },
    });
    setReason('');
    setSelectedSiteOption(preferredSite);
    history.push(`/receive/${RECEIVE_TYPES_UI.INVENTORY_ADJUSTMENT}`);
  };

  const handleMouseOver = () => {
    setShowNoteAlert(true);
  };

  const handleMouseOut = () => {
    setShowNoteAlert(false);
  };

  const handleSiteChange = (obj: any) => {
    setSelectedSiteOption(obj);

    // Update the model should it be necessary
    if (!modelExists && urlParam.referenceId && reference) {
      dispatchInventoryAdjustmentScanReference(urlParam.referenceId, undefined, obj);
      return;
    }
  };

  useEffect(() => {
    if (!selectedSiteOption) {
      setSelectedSiteOption(preferredSite);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [preferredSite]);

  const handleOnReasonCodeChange = (reason: string) => {
    let temporarySiteOption = selectedSiteOption;
    if (!selectedSiteOption) {
      setSelectedSiteOption(preferredSite);
      temporarySiteOption = preferredSite;
    }
    setReason(reason);

    // Update the model should it be necessary
    if (!modelExists && urlParam?.referenceId && reference) {
      dispatchInventoryAdjustmentScanReference(urlParam.referenceId, reason, temporarySiteOption);
      return;
    }

    const referenceId = uuidv4();
    dispatchInventoryAdjustmentScanReference(referenceId, reason, temporarySiteOption);

    const receiveType = RECEIVE_TYPES_UI.INVENTORY_ADJUSTMENT;
    history.push(`/receive/${receiveType}/${referenceId}`);
  };

  /**
   * Perform a dispatch({type: Types.scanReference}) with default parameters.
   * @param referenceId to update
   * @param newReason (optional) new reason code - defaults to existing reason code if not provided
   * @param newSelectedSiteOption (optional) new selected site option - defaults to existing site option if not provided
   */
  const dispatchInventoryAdjustmentScanReference = (referenceId: string, newReason?: any, newSelectedSiteOption?: any) => {
    const adjustedReason = !!newReason ? newReason : reason;
    const adjustedSelectedSiteOption = !!newSelectedSiteOption ? newSelectedSiteOption : selectedSiteOption
    dispatch({
      type: Types.scanReference,
      payload: {
        data: {
          receiveReferenceQueryResponseList: [
            {
              receiveReference: {
                receiveReferenceId: referenceId,
                externalReferenceValue: '',
                externalReferenceType: ReceiveType.INVENTORY_ADJUSTMENT,
                expectedReceiveLocation: !!adjustedSelectedSiteOption
                    ? adjustedSelectedSiteOption.locationId
                    : preferredSite && preferredSite?.locationId,
                expectedDeliveryDateTime: '',
                supplier: '',
                trackingId: '',
                carrier: '',
                siteName: !!adjustedSelectedSiteOption
                    ? adjustedSelectedSiteOption.value
                    : preferredSite && preferredSite?.value,
                reasonCode: adjustedReason,
                clusterName: undefined,
                referenceStatus: null,
                scannableId: '',
              },
              scannableIdFoundAs: 'INVENTORY_ADJUSTMENT',
              status: {
                validationStatusType: ValidationStatusType.OK,
                statusCode: undefined,
                statusMessage: undefined,
              },
            },
          ],
        },
        loading: false,
        errors: [],
      },
    });
    updateBoostMetaDataFn(adjustedReason, adjustedSelectedSiteOption);
  }

  const REASON_CODES = [
    {
      label: CYCLE_COUNT_ADJUSTMENT_LABEL,
      value: CYCLE_COUNT_ADJUSTMENT_VALUE,
      description: <span className="description">{CYCLE_COUNT_ADJUSTMENT_DESCRIPTION}</span>,
    },
    {
      label: RECOVERED_GEAR_LABEL,
      value: RECOVERED_GEAR_VALUE,
      description: <span className="description">{RECOVERED_GEAR_DESCRIPTION}</span>,
    },
    {
      label: (
        <div>
          <div onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
            <SpaceBetween size="xs" direction="horizontal">
              {REHARVESTED_GEAR_LABEL}
              <Icon name="status-info" />
            </SpaceBetween>
          </div>
        </div>
      ),
      value: REHARVESTED_GEAR_VALUE,
      description: (
        <SpaceBetween size="xs">
          {REHARVESTED_GEAR_DESCRIPTION}
          {showNoteAlert && <Alert>{REHARVESTED_GEAR_DESCRIPTION_NOTE}</Alert>}
        </SpaceBetween>
      ),
    },
  ];

  // Block the inventory-adjustment form when the user's effective site (the inner
  // SiteInput overrides preferredSite) is past its deprecation cutoff. Without this
  // check, users could pick e.g. IAD057 / IAD058 here and proceed even though those
  // sites have migrated to Boost. See src/constants/siteDeprecations.ts.
  const effectiveSite = selectedSiteOption?.value || preferredSite?.value;
  const deprecation = getSiteDeprecationStatus(effectiveSite);
  const isSiteBlocked = deprecation.status === 'blocked';

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            reason &&
            selectedSiteOption &&
            !isBoostWidget && (
              <Button iconName="close" onClick={onHandleConfirmClear} ariaLabel="Start new shipment">
                Start new receive
              </Button>
            )
          }
        ></Header>
      }
    >
      <div className={"ia-warning"}>
        <SpaceBetween size={"xs"} direction={"horizontal"}>
          <div>
            <strong><u>Important Note:</u> DO NOT</strong> receive <strong>Janus</strong> parts using the Inventory Adjustment
            Tool. If you encounter any issues with Janus shipments, please cut a ticket <a
            href="https://issues.amazon.com/issues/create?template=b9373c18-d5a4-4a2a-b93d-12da60ab744f" target="_blank" rel="noreferrer">here.</a>
          </div>
        </SpaceBetween>
      </div>

      <Grid gridDefinition={[{ colspan: 1.5 }, { colspan: 8 }]}>
        <div>
          <ValueWithLabel label="Receiving Site">
            <SiteInput
                siteName={selectedSiteOption?.value}
                isPreferredSite={false}
                onSiteChange={handleSiteChange}
                isDisabled={!!modelExists}/>
          </ValueWithLabel>
        </div>
        <div>
          {isSiteBlocked ? (
            <Alert
              statusIconAriaLabel="Error"
              type="error"
              header="This site is no longer available"
            >
              {deprecation.message}
              {deprecation.boostLink && (
                <>
                  {' '}
                  <Link href={deprecation.boostLink} external>
                    Go to Boost Receive.
                  </Link>
                </>
              )}
            </Alert>
          ) : (
            <ValueWithLabel label=" Receiving Reason">
              <RadioGroup
                ariaLabel={`selected reason code ${reason}`}
                onChange={({ detail }) => {
                  handleOnReasonCodeChange(detail.value);
                }}
                value={reason}
                items={REASON_CODES.map((r) => ({ ...r, disabled: reason && reason !== r.value && modelExists }))}
              />
            </ValueWithLabel>
          )}
        </div>
      </Grid>
    </Container>
  );
}
